/**
 * @fileoverview Reference data fetching and location cascade handlers for Register.
 * @module pages/register/useRegisterReferenceData
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { getReferenceData } from "../../logic/companyEdit";
import { referenceIdsEqual } from "./registerReferenceIds.js";

const useRegisterReferenceData = ({ setValues, setLoading }) => {
  const navigate = useNavigate();

  const countryRef = useRef(null);
  const stateRef = useRef(null);
  const townRef = useRef(null);
  const zipCodeRef = useRef(null);
  const neighborhoodRef = useRef(null);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [generalTowns, setGeneralTowns] = useState([]);
  const [towns, setTowns] = useState([]);
  const [generalZipCodes, setGeneralZipCodes] = useState([]);
  const [zipCodes, setZipCodes] = useState([]);
  const [generalNeighborhoods, setGeneralNeighborhoods] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);

  const fetchReferenceData = useCallback(async () => {
    try {
      logger.info("Fetching reference data...");
      setLoading(true);

      const [
        countriesData,
        statesData,
        townsData,
        zipCodesData,
        neighborhoodsData,
      ] = await Promise.all([
        getReferenceData("countries", navigate),
        getReferenceData("states", navigate),
        getReferenceData("towns", navigate),
        getReferenceData("zipCodes", navigate),
        getReferenceData("neighborhoods", navigate),
      ]);

      setCountries(
        countriesData && countriesData.length > 0 ? countriesData : [],
      );
      setStates(statesData && statesData.length > 0 ? statesData : []);
      setTowns(townsData && townsData.length > 0 ? townsData : []);
      setZipCodes(zipCodesData && zipCodesData.length > 0 ? zipCodesData : []);
      setNeighborhoods(
        neighborhoodsData && neighborhoodsData.length > 0
          ? neighborhoodsData
          : [],
      );
      setGeneralTowns(townsData && townsData.length > 0 ? townsData : []);
      setGeneralZipCodes(
        zipCodesData && zipCodesData.length > 0 ? zipCodesData : [],
      );
      setGeneralNeighborhoods(
        neighborhoodsData && neighborhoodsData.length > 0
          ? neighborhoodsData
          : [],
      );
    } catch (err) {
      logger.error("Error fetching reference data:", {
        err: sanitizeError(err),
      });
      setCountries([]);
      setStates([]);
      setTowns([]);
      setZipCodes([]);
      setNeighborhoods([]);
    } finally {
      setLoading(false);
    }
  }, [navigate, setLoading]);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  const handleCountryChange = useCallback((selectedOption) => {
    if (!selectedOption) {
      return;
    }
    setValues((prev) => ({ ...prev, pais: selectedOption._id }));
  }, [setValues]);

  const handleZipCodeChange = useCallback(
    (selectedOption) => {
      if (!selectedOption) {
        return;
      }

      const filteredNeighborhoods = generalNeighborhoods.filter((neighborhood) =>
        referenceIdsEqual(neighborhood.idCodigoPostal, selectedOption._id),
      );
      setNeighborhoods(filteredNeighborhoods);
      const firstNeighborhood = filteredNeighborhoods[0];
      const currentTown = generalTowns.find((town) =>
        referenceIdsEqual(town._id, firstNeighborhood.idMunicipio),
      );
      setValues((prev) => ({
        ...prev,
        codigoPostal: selectedOption._id,
        municipio: firstNeighborhood.idMunicipio,
        estado: String(currentTown.idEstado),
      }));
    },
    [generalNeighborhoods, generalTowns, setValues],
  );

  const handleStateChange = useCallback(
    (selectedOption) => {
      if (!selectedOption) {
        return;
      }

      setValues((prev) => ({
        ...prev,
        estado: selectedOption._id,
        municipio: "",
        codigoPostal: "",
        colonia: "",
      }));
      townRef.current?.clearValue();
      zipCodeRef.current?.clearValue();
      neighborhoodRef.current?.clearValue();

      setTowns(
        generalTowns.filter((town) =>
          referenceIdsEqual(town.idEstado, selectedOption._id),
        ),
      );
      setNeighborhoods(generalNeighborhoods);
      setZipCodes(generalZipCodes);
    },
    [generalNeighborhoods, generalTowns, generalZipCodes, setValues],
  );

  const handleTownChange = useCallback(
    (selectedOption) => {
      if (!selectedOption) {
        return;
      }

      setValues((prev) => ({
        ...prev,
        municipio: selectedOption._id,
        codigoPostal: "",
      }));
      zipCodeRef.current?.clearValue();

      const filteredNeighborhoods = generalNeighborhoods.filter((neighborhood) =>
        referenceIdsEqual(neighborhood.idMunicipio, selectedOption._id),
      );
      const filteredZipCodes = generalZipCodes.filter((zipcode) =>
        filteredNeighborhoods.some((neighborhood) =>
          referenceIdsEqual(neighborhood.idCodigoPostal, zipcode._id),
        ),
      );
      setNeighborhoods(filteredNeighborhoods);
      setZipCodes(filteredZipCodes);
    },
    [generalNeighborhoods, generalZipCodes, setValues],
  );

  const handleNeighborhoodChange = useCallback(
    (selectedOption) => {
      if (!selectedOption) {
        return;
      }

      setValues((prev) => ({ ...prev, colonia: selectedOption._id }));

      const filteredNeighborhoods = generalNeighborhoods.filter(
        (neighborhood) =>
          referenceIdsEqual(
            neighborhood.idCodigoPostal,
            selectedOption.idCodigoPostal,
          ) &&
          referenceIdsEqual(
            neighborhood.idMunicipio,
            selectedOption.idMunicipio,
          ),
      );
      setNeighborhoods(filteredNeighborhoods);

      const filteredZipCodes = generalZipCodes.filter((zipcode) =>
        referenceIdsEqual(zipcode._id, selectedOption.idCodigoPostal),
      );
      setZipCodes(filteredZipCodes);

      const filteredTowns = generalTowns.filter((town) =>
        referenceIdsEqual(town._id, selectedOption.idMunicipio),
      );
      setValues((prev) => ({
        ...prev,
        codigoPostal: selectedOption.idCodigoPostal,
        municipio: selectedOption.idMunicipio,
        estado: String(filteredTowns[0].idEstado),
      }));
    },
    [generalNeighborhoods, generalZipCodes, generalTowns, setValues],
  );

  return {
    countries,
    states,
    towns,
    zipCodes,
    neighborhoods,
    countryRef,
    stateRef,
    townRef,
    zipCodeRef,
    neighborhoodRef,
    handleCountryChange,
    handleZipCodeChange,
    handleStateChange,
    handleTownChange,
    handleNeighborhoodChange,
  };
};

export default useRegisterReferenceData;
